import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  Book,
  CheckCircle,
  FileText,
  Printer,
  RefreshCw,
  RotateCcw,
  Save,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import ReactDOM from 'react-dom';
import MainService from '../../services/MainService';
import { getApiBaseUrl } from '../../services/appSettings';

const PAGE_SIZE = 20;

const emptyVoucherBook = {
  BookNo: '',
  VoucherNo: '',
  ClientId: '',
  Client: { Name: '', MobileNo1: '' },
  Vouchers: '',
  VoucherFrom: '',
  VoucherTo: '',
};

const emptyPrintFilter = {
  BookNo: '',
  ClientId: '',
  Client: { Name: '', MobileNo1: '' },
};

const parseList = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.Data)) return response.Data;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const getStatus = (response) => (typeof response === 'string' ? response.trim() : String(response?.Status ?? response?.status ?? '').trim());

const getMessage = (response, fallback) => {
  if (typeof response === 'string' && response.trim()) return response.trim();
  return response?.Message || response?.message || response?.Status || response?.status || fallback;
};

const buildClientRequest = (keyword, field = 'Name') => ({
  filter: {
    filters: [{ field, operator: 'contains', value: keyword }],
    logic: 'and',
  },
});

const VoucherBook = () => {
  const [formData, setFormData] = useState(emptyVoucherBook);
  const [printFilter, setPrintFilter] = useState(emptyPrintFilter);
  const [rows, setRows] = useState([]);
  const [editedRows, setEditedRows] = useState({});
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSearchKeyword, setActiveSearchKeyword] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingRowId, setSavingRowId] = useState(null);
  const [printing, setPrinting] = useState(false);
  const [clientOptions, setClientOptions] = useState([]);
  const [clientLoading, setClientLoading] = useState(false);
  const [clientPicker, setClientPicker] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const clientSearchSeq = useRef(0);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const selectedCount = selectedIds.size;

  const loadRows = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await MainService.VoucherBookInList({
        Keyword: activeSearchKeyword,
        Skip: (currentPage - 1) * PAGE_SIZE,
        Take: PAGE_SIZE,
      });

      setRows(parseList(response));
      setTotalCount(Number(response?.Total) || 0);
      setEditedRows({});
      setSelectedIds(new Set());
    } catch (err) {
      console.error('Load voucher books error:', err);
      setError('Failed to load voucher books');
      setRows([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [activeSearchKeyword, currentPage]);

  const loadClients = useCallback(async (keyword, field = 'Name') => {
    const trimmed = keyword.trim();
    if (trimmed.length < 2) {
      setClientOptions([]);
      return;
    }

    const seq = clientSearchSeq.current + 1;
    clientSearchSeq.current = seq;

    try {
      setClientLoading(true);
      const response = await MainService.ClientsInAutoComplete(buildClientRequest(trimmed, field));
      if (seq !== clientSearchSeq.current) return;
      setClientOptions(parseList(response));
    } catch (err) {
      console.error('Load clients error:', err);
      if (seq === clientSearchSeq.current) setClientOptions([]);
    } finally {
      if (seq === clientSearchSeq.current) setClientLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  useEffect(() => {
    if (!error) return undefined;
    const timer = setTimeout(() => setError(''), 6000);
    return () => clearTimeout(timer);
  }, [error]);

  useEffect(() => {
    if (!success) return undefined;
    const timer = setTimeout(() => setSuccess(''), 3500);
    return () => clearTimeout(timer);
  }, [success]);

  useEffect(() => {
    if (!clientPicker) return undefined;
    const timer = setTimeout(() => loadClients(clientPicker.keyword, clientPicker.field), 250);
    return () => clearTimeout(timer);
  }, [clientPicker, loadClients]);

  const selectedRows = useMemo(() => rows.filter((row) => selectedIds.has(row.Id)), [rows, selectedIds]);

  const resetForm = () => {
    setFormData(emptyVoucherBook);
    setClientPicker(null);
    setClientOptions([]);
  };

  const setClientValue = (target, patch) => {
    const setter = target === 'print' ? setPrintFilter : setFormData;
    setter((prev) => ({
      ...prev,
      ...patch,
      Client: { ...(prev.Client || {}), ...(patch.Client || {}) },
    }));
  };

  const selectClient = (client) => {
    if (!clientPicker) return;
    const target = clientPicker.target;
    setClientValue(target, {
      ClientId: client.Id,
      Client: {
        Id: client.Id,
        Name: client.Name || '',
        MobileNo1: client.MobileNo1 || '',
      },
    });
    setClientPicker(null);
    setClientOptions([]);
  };

  const convertRange = () => {
    const from = Number(formData.VoucherFrom);
    const to = Number(formData.VoucherTo);

    if (!Number.isFinite(from) || from <= 0) {
      setError('Enter Voucher From');
      return;
    }

    if (!Number.isFinite(to) || to <= 0) {
      setError('Enter Voucher To');
      return;
    }

    if (to < from) {
      setError('Voucher To must be greater than or equal to Voucher From');
      return;
    }

    const vouchers = [];
    for (let i = from; i <= to; i += 1) {
      vouchers.push(String(i));
    }
    setFormData((prev) => ({ ...prev, Vouchers: vouchers.join('.') }));
  };

  const getVoucherArray = () => (
    String(formData.Vouchers || '')
      .split('.')
      .map((value) => value.trim())
      .filter(Boolean)
  );

  const saveVoucherBook = async () => {
    const vouchers = getVoucherArray();

    if (vouchers.length === 0) {
      setError('Enter Voucher Nos');
      return;
    }

    if (!formData.ClientId) {
      setError('Select Client');
      return;
    }

    if (!formData.BookNo.trim()) {
      setError('Enter Book Number');
      return;
    }

    try {
      setSaving(true);
      setError('');
      const payload = vouchers.map((voucherNo) => ({
        VoucherNo: voucherNo,
        BookNo: formData.BookNo.trim(),
        ClientId: formData.ClientId,
      }));

      const response = await MainService.SaveVoucherBook(payload);
      const status = getStatus(response);
      if (status === 'Success' || response === true || response === 'true') {
        setSuccess('Voucher book saved successfully');
      } else {
        setSuccess(getMessage(response, 'Voucher book saved with warnings'));
      }

      resetForm();
      setCreateOpen(false);
      await loadRows();
    } catch (err) {
      console.error('Save voucher book error:', err);
      setError('Network Error');
    } finally {
      setSaving(false);
    }
  };

  const handleRowVoucherChange = (row, value) => {
    setEditedRows((prev) => ({
      ...prev,
      [row.Id]: {
        ...row,
        ...prev[row.Id],
        VoucherNo: value,
      },
    }));
  };

  const getRowVoucherNo = (row) => editedRows[row.Id]?.VoucherNo ?? row.VoucherNo ?? '';

  const isRowChanged = (row) => String(getRowVoucherNo(row)) !== String(row.VoucherNo ?? '');

  const resetRow = (row) => {
    setEditedRows((prev) => {
      const next = { ...prev };
      delete next[row.Id];
      return next;
    });
  };

  const updateVoucherBook = async (row) => {
    const voucherNo = String(getRowVoucherNo(row)).trim();
    if (!voucherNo) {
      setError('Enter Voucher No');
      return;
    }

    try {
      setSavingRowId(row.Id);
      setError('');
      const response = await MainService.UpdateVoucherBook({
        ...row,
        VoucherNo: voucherNo,
      });

      const status = getStatus(response);
      if (status === 'Success' || response === true || response === 'true') {
        setSuccess('Voucher updated successfully');
        await loadRows();
      } else {
        setError(getMessage(response, 'Failed to update voucher'));
      }
    } catch (err) {
      console.error('Update voucher book error:', err);
      setError('Network Error');
    } finally {
      setSavingRowId(null);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      if (rows.length > 0 && rows.every((row) => prev.has(row.Id))) return new Set();
      return new Set(rows.map((row) => row.Id));
    });
  };

  const deleteVoucher = async (row) => {
    if (!row?.Id) return;
    try {
      setLoading(true);
      const response = await MainService.DeleteVoucherBook(row.Id);
      const status = getStatus(response);
      if (status === 'Success' || response === true || response === 'true') {
        setSuccess('Deleted');
        setDeleteTarget(null);
        await loadRows();
      } else {
        setError(getMessage(response, 'Failed to delete voucher'));
      }
    } catch (err) {
      console.error('Delete voucher book error:', err);
      setError('Network Error');
    } finally {
      setLoading(false);
    }
  };

  const deleteSelected = async () => {
    if (selectedIds.size === 0) {
      setError('Select vouchers');
      return;
    }

    try {
      setLoading(true);
      const response = await MainService.DeleteBulkVoucherBook({ BookIds: Array.from(selectedIds) });
      const status = getStatus(response);
      if (status === 'Success' || response === true || response === 'true') {
        setSuccess('Deleted');
        setBulkDeleteOpen(false);
        await loadRows();
      } else {
        setError(getMessage(response, 'Failed to delete selected vouchers'));
      }
    } catch (err) {
      console.error('Bulk delete voucher book error:', err);
      setError('Network Error');
    } finally {
      setLoading(false);
    }
  };

  const printVoucherBook = async () => {
    if (!printFilter.BookNo.trim() && !printFilter.ClientId) {
      setError('Choose Client or Enter Book No');
      return;
    }

    try {
      setPrinting(true);
      setError('');
      const response = await MainService.PrintVoucherBook({
        BookNo: printFilter.BookNo.trim(),
        ClientId: printFilter.ClientId || 0,
      });

      if (response) {
        window.open(`${getApiBaseUrl()}ReportFiles/${response}`, '_blank');
      } else {
        setError('Print Error');
      }
    } catch (err) {
      console.error('Print voucher book error:', err);
      setError('Print Error');
    } finally {
      setPrinting(false);
    }
  };

  const applySearch = () => {
    setCurrentPage(1);
    setActiveSearchKeyword(searchTerm.trim());
  };

  const clearSearch = () => {
    setSearchTerm('');
    setActiveSearchKeyword('');
    setCurrentPage(1);
  };

  const clientDropdown = () => {
    if (!clientPicker) return null;

    return (
      <div className="absolute z-40 mt-1 max-h-72 w-full overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-xl">
        {clientLoading ? (
          <div className="flex items-center gap-2 px-4 py-3 text-sm text-gray-500">
            <RefreshCw className="h-4 w-4 animate-spin" />
            Loading clients...
          </div>
        ) : clientOptions.length === 0 ? (
          <div className="px-4 py-3 text-sm text-gray-500">Type at least 2 letters or numbers to search clients.</div>
        ) : (
          clientOptions.map((client) => (
            <button
              key={client.Id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => selectClient(client)}
              className="block w-full px-4 py-3 text-left hover:bg-gray-50"
            >
              <div className="text-sm font-semibold text-gray-900">{client.Name || '-'}</div>
              <div className="text-xs text-gray-500">{client.MobileNo1 || '-'}</div>
            </button>
          ))
        )}
      </div>
    );
  };

  const clientInput = ({ target, field, label, placeholder, value }) => (
    <div className="relative">
      <label className="mb-1 block text-sm font-medium text-gray-700">{label}</label>
      <input
        value={value || ''}
        onChange={(e) => {
          const nextValue = e.target.value;
          setClientValue(target, {
            ClientId: '',
            Client: { [field === 'MobileNo1' ? 'MobileNo1' : 'Name']: nextValue },
          });
          setClientPicker({ target, field, keyword: nextValue });
        }}
        onFocus={() => setClientPicker({ target, field, keyword: value || '' })}
        onBlur={() => setTimeout(() => setClientPicker(null), 150)}
        placeholder={placeholder}
        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary"
      />
      {clientPicker?.target === target && clientPicker?.field === field ? clientDropdown() : null}
    </div>
  );

  return (
    <div className="space-y-6">
      {error && (
        <div className="fixed right-4 top-20 z-50 flex min-w-[320px] items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700 shadow-lg">
          <AlertTriangle className="h-5 w-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button type="button" onClick={() => setError('')} className="text-red-500 hover:text-red-700">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="fixed right-4 top-20 z-50 flex min-w-[320px] items-center gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-green-700 shadow-lg">
          <CheckCircle className="h-5 w-5 flex-shrink-0" />
          <span className="flex-1">{success}</span>
          <button type="button" onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-primary/10 p-3 text-primary">
              <Book className="h-7 w-7" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Voucher Book</h1>
              <p className="mt-1 text-sm text-gray-500">Create voucher books, update voucher numbers, print, and delete unused vouchers.</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-green-700"
            >
              <Book className="h-4 w-4" />
              Create Voucher Book
            </button>
            <button
              type="button"
              onClick={() => setPrintOpen(true)}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
            >
              <Printer className="h-4 w-4" />
              Print
            </button>
            <button
              type="button"
              onClick={loadRows}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-lg bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200 disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>

        <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-100 p-5">
            <div className="flex flex-col gap-4 2xl:flex-row 2xl:items-center 2xl:justify-between">
              <div>
                <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                  <FileText className="h-5 w-5 text-primary" />
                  Voucher Book List
                </h2>
                <p className="mt-1 text-sm text-gray-500">Showing 20 vouchers per page. Selected: {selectedCount}</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') applySearch();
                    }}
                    className="w-64 rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Search book no / client"
                  />
                </div>
                <button type="button" onClick={applySearch} className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-green-700">
                  Search
                </button>
                <button type="button" onClick={clearSearch} className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200">
                  Clear
                </button>
                <button
                  type="button"
                  onClick={() => setBulkDeleteOpen(true)}
                  disabled={selectedCount === 0 || loading}
                  className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete Selected
                </button>
              </div>
            </div>
          </div>

          <div className="max-h-[640px] overflow-auto">
            <table className="w-full">
              <thead className="sticky top-0 z-10 bg-gray-50 shadow-sm">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={rows.length > 0 && rows.every((row) => selectedIds.has(row.Id))}
                      onChange={toggleSelectAll}
                      className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Client</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Book No</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Voucher No</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Status</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-500">Options</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-16 text-center text-gray-500">
                      <RefreshCw className="mx-auto mb-2 h-8 w-8 animate-spin text-primary" />
                      Loading voucher books...
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-16 text-center text-gray-500">No voucher books found</td>
                  </tr>
                ) : (
                  rows.map((row) => {
                    const changed = isRowChanged(row);
                    return (
                      <tr key={row.Id} className={changed ? 'bg-green-50/60 hover:bg-green-50' : 'hover:bg-gray-50'}>
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(row.Id)}
                            onChange={() => toggleSelect(row.Id)}
                            className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm font-semibold text-gray-900">{row.Client?.Name || '-'}</div>
                          <div className="text-xs text-gray-500">{row.Client?.MobileNo1 || '-'}</div>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">{row.BookNo || '-'}</td>
                        <td className="px-4 py-3">
                          <input
                            value={getRowVoucherNo(row)}
                            onChange={(e) => handleRowVoucherChange(row, e.target.value)}
                            disabled={row.IsUsed}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary disabled:bg-gray-100 disabled:text-gray-500"
                          />
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${row.IsUsed ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                            {row.IsUsed ? 'Used' : 'Not Used'}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-2">
                            {changed ? (
                              <button
                                type="button"
                                onClick={() => resetRow(row)}
                                className="rounded-lg p-2 text-gray-600 hover:bg-gray-100"
                                title="Reset"
                              >
                                <RotateCcw className="h-4 w-4" />
                              </button>
                            ) : null}
                            <button
                              type="button"
                              onClick={() => updateVoucherBook(row)}
                              disabled={savingRowId === row.Id || loading || row.IsUsed || !changed}
                              className="rounded-lg p-2 text-primary hover:bg-green-50 disabled:opacity-50"
                              title="Save"
                            >
                              {savingRowId === row.Id ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                            </button>
                            <button
                              type="button"
                              onClick={() => setDeleteTarget(row)}
                              disabled={loading}
                              className="rounded-lg p-2 text-red-600 hover:bg-red-50 disabled:opacity-50"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col gap-3 border-t border-gray-100 bg-gray-50 px-5 py-4 md:flex-row md:items-center md:justify-between">
            <p className="text-sm text-gray-600">
              Showing {rows.length ? (currentPage - 1) * PAGE_SIZE + 1 : 0} to {Math.min(currentPage * PAGE_SIZE, totalCount)} of {totalCount}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage <= 1 || loading}
                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <span className="text-sm text-gray-700">Page {currentPage} of {totalPages}</span>
              <button
                type="button"
                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={currentPage >= totalPages || loading}
                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </div>

      {createOpen && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="max-h-[92vh] w-full max-w-4xl overflow-hidden rounded-xl bg-white shadow-2xl">
            <div className="flex items-start gap-3 border-b border-gray-100 px-5 py-4">
              <div className="rounded-lg bg-primary/10 p-2 text-primary">
                <Book className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Create Voucher Book</h3>
                <p className="mt-1 text-sm text-gray-500">Select client, enter book number, then generate or paste voucher numbers.</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setCreateOpen(false);
                  setClientPicker(null);
                }}
                disabled={saving}
                className="ml-auto rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="max-h-[calc(92vh-140px)] overflow-y-auto p-5">
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                <div className="mb-3 flex items-center gap-2">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-xs font-bold text-white">1</span>
                  <h4 className="font-semibold text-gray-900">Client & Book</h4>
                </div>
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                  {clientInput({
                    target: 'form',
                    field: 'Name',
                    label: 'Client',
                    placeholder: 'Search by client name',
                    value: formData.Client?.Name,
                  })}
                  {clientInput({
                    target: 'form',
                    field: 'MobileNo1',
                    label: 'Client Mobile No',
                    placeholder: 'Search by mobile no',
                    value: formData.Client?.MobileNo1,
                  })}
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">Book No</label>
                    <input
                      value={formData.BookNo}
                      onChange={(e) => setFormData((prev) => ({ ...prev, BookNo: e.target.value }))}
                      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="Enter book number"
                    />
                  </div>
                </div>
              </div>

              <div className="mt-4 rounded-xl border border-gray-200 bg-white p-4">
                <div className="mb-3 flex items-center gap-2">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-xs font-bold text-white">2</span>
                  <h4 className="font-semibold text-gray-900">Generate Vouchers</h4>
                </div>
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_auto_1.4fr] lg:items-end">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">Voucher From</label>
                      <input
                        type="number"
                        value={formData.VoucherFrom}
                        onChange={(e) => setFormData((prev) => ({ ...prev, VoucherFrom: e.target.value }))}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary"
                        min="0"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">Voucher To</label>
                      <input
                        type="number"
                        value={formData.VoucherTo}
                        onChange={(e) => setFormData((prev) => ({ ...prev, VoucherTo: e.target.value }))}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary"
                        min="0"
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={convertRange}
                    className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200"
                  >
                    Convert
                  </button>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">Voucher Nos</label>
                    <textarea
                      value={formData.Vouchers}
                      onChange={(e) => setFormData((prev) => ({ ...prev, Vouchers: e.target.value }))}
                      rows={4}
                      placeholder="ex: 1322.ab123.343a.776757"
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                    <p className="mt-1 text-xs text-gray-500">{getVoucherArray().length} voucher(s) ready to save</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 border-t border-gray-100 bg-gray-50 px-5 py-4">
              <button
                type="button"
                onClick={resetForm}
                disabled={saving}
                className="inline-flex items-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
              >
                <RotateCcw className="h-4 w-4" />
                Clear
              </button>
              <button
                type="button"
                onClick={saveVoucherBook}
                disabled={saving}
                className="inline-flex min-w-32 items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-50"
              >
                {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {printOpen && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="w-full max-w-2xl overflow-hidden rounded-xl bg-white shadow-2xl">
            <div className="flex items-start gap-3 border-b border-gray-100 px-5 py-4">
              <div className="rounded-lg bg-blue-100 p-2 text-blue-700">
                <Printer className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Print Voucher Book</h3>
                <p className="mt-1 text-sm text-gray-500">Enter Book No or choose a client.</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setPrintOpen(false);
                  setClientPicker(null);
                }}
                disabled={printing}
                className="ml-auto rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="grid grid-cols-1 gap-4 p-5 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Book No</label>
                <input
                  value={printFilter.BookNo}
                  onChange={(e) => setPrintFilter((prev) => ({ ...prev, BookNo: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Enter book number"
                />
              </div>
              {clientInput({
                target: 'print',
                field: 'Name',
                label: 'Client',
                placeholder: 'Search by client name',
                value: printFilter.Client?.Name,
              })}
            </div>
            <div className="flex justify-end gap-2 border-t border-gray-100 bg-gray-50 px-5 py-4">
              <button
                type="button"
                onClick={() => setPrintFilter(emptyPrintFilter)}
                disabled={printing}
                className="rounded-lg bg-white px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
              >
                Clear
              </button>
              <button
                type="button"
                onClick={printVoucherBook}
                disabled={printing}
                className="inline-flex min-w-32 items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {printing ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
                Print
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {deleteTarget && ReactDOM.createPortal(
        <ConfirmDialog
          title="Delete Voucher"
          message={`Delete voucher "${deleteTarget.VoucherNo}"?`}
          loading={loading}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => deleteVoucher(deleteTarget)}
        />,
        document.body
      )}

      {bulkDeleteOpen && ReactDOM.createPortal(
        <ConfirmDialog
          title="Delete Selected Vouchers"
          message={`Delete ${selectedRows.length} selected voucher(s)?`}
          loading={loading}
          onCancel={() => setBulkDeleteOpen(false)}
          onConfirm={deleteSelected}
        />,
        document.body
      )}
    </div>
  );
};

const ConfirmDialog = ({ title, message, loading, onCancel, onConfirm }) => (
  <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black bg-opacity-50">
    <div className="mx-4 w-full max-w-md overflow-hidden rounded-xl bg-white shadow-2xl">
      <div className="flex items-start gap-3 border-b border-gray-100 px-5 py-4">
        <div className="rounded-full bg-red-100 p-2 text-red-600">
          <AlertTriangle className="h-5 w-5" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <p className="mt-1 text-sm text-gray-500">{message}</p>
        </div>
        <button type="button" onClick={onCancel} disabled={loading} className="ml-auto rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
          <X className="h-5 w-5" />
        </button>
      </div>
      <div className="flex justify-end gap-3 border-t border-gray-100 bg-gray-50 px-5 py-4">
        <button type="button" onClick={onCancel} disabled={loading} className="rounded-lg bg-white px-4 py-2 text-sm font-medium text-gray-700 ring-1 ring-gray-300 hover:bg-gray-50 disabled:opacity-50">
          Cancel
        </button>
        <button type="button" onClick={onConfirm} disabled={loading} className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50">
          {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
          Confirm
        </button>
      </div>
    </div>
  </div>
);

export default VoucherBook;
